import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { z } from "zod";
import { ArrowLeft, Save, Upload, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

// Input validation schemas
const companySettingsSchema = z.object({
  company_name: z
    .string()
    .trim()
    .min(1, { message: "Company name is required" })
    .max(100, { message: "Company name must be less than 100 characters" }),
  company_address: z
    .string()
    .trim()
    .max(200, { message: "Address must be less than 200 characters" }),
  company_phone: z
    .string()
    .trim()
    .max(20, { message: "Phone must be less than 20 characters" }),
  company_email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email" })
    .max(100, { message: "Email must be less than 100 characters" }),
  company_website: z
    .string()
    .trim()
    .max(100, { message: "Website must be less than 100 characters" }),
  tagline: z
    .string()
    .trim()
    .max(200, { message: "Tagline must be less than 200 characters" }),
});

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: "Foundry Cabinet Co.",
    company_logo: "",
    company_address: "866 N Columbia Blvd suite b 111, Portland, OR 97217",
    company_phone: "(971) 272-0955",
    company_email: "info@foundrypdx.com",
    company_website: "www.foundrypdx.com",
    tagline: "Driven by Precision. Evolved by Design.",
  });
  const [mediaLibrary, setMediaLibrary] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadSettings();
    loadMedia();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Check if user has admin role
      const { data: isAdmin } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });

      if (!isAdmin) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/");
        return;
      }

      const { data, error } = await supabase
        .from("company_settings")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data);
      } else {
        // Create default settings
        const { error: insertError } = await supabase
          .from("company_settings")
          .insert([{ user_id: user.id, ...settings }]);
        
        if (insertError) throw insertError;
      }
    } catch (error: any) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadMedia = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("media_library")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMediaLibrary(data || []);
    } catch (error: any) {
      // Silent fail for media loading
    }
  };

  const saveSettings = async () => {
    // Validate settings
    const validation = companySettingsSchema.safeParse({
      company_name: settings.company_name,
      company_address: settings.company_address,
      company_phone: settings.company_phone,
      company_email: settings.company_email,
      company_website: settings.company_website,
      tagline: settings.tagline,
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updatePayload = { ...validation.data, company_logo: settings.company_logo };
      const { error } = await supabase
        .from("company_settings")
        .update(updatePayload)
        .eq("user_id", user.id);

      if (error) throw error;
      toast.success("Settings saved successfully!");
    } catch (error: any) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async (file: File, category: string) => {
    // Validate file
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Only JPEG, PNG, and WebP images are allowed");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (file.name.length > 255) {
      toast.error("Filename is too long");
      return;
    }

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("proposal-media")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("proposal-media")
        .getPublicUrl(fileName);

      // Sanitize caption
      const caption = file.name.replace(/\.[^/.]+$/, "").slice(0, 200);

      const { error: dbError } = await supabase
        .from("media_library")
        .insert([{
          user_id: user.id,
          filename: file.name.slice(0, 255),
          category,
          image_url: publicUrl,
          caption,
        }]);

      if (dbError) throw dbError;

      toast.success("Photo uploaded!");
      loadMedia();
    } catch (error: any) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (id: string, imageUrl: string) => {
    if (!confirm("Delete this photo?")) return;

    try {
      const { error } = await supabase
        .from("media_library")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Extract file path from URL
      const urlParts = imageUrl.split("/proposal-media/");
      if (urlParts[1]) {
        await supabase.storage
          .from("proposal-media")
          .remove([urlParts[1]]);
      }

      toast.success("Photo deleted!");
      loadMedia();
    } catch (error: any) {
      console.error("Error deleting media:", error);
      toast.error("Failed to delete photo");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button onClick={() => navigate("/")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Builder
          </Button>
          <h1 className="text-3xl font-light text-slate-800">Admin Settings</h1>
        </div>

        <Tabs defaultValue="company" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="company">Company Info</TabsTrigger>
            <TabsTrigger value="media">Media Library</TabsTrigger>
          </TabsList>

          <TabsContent value="company">
            <Card className="p-6">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={settings.company_name}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Logo Upload</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const logoData = event.target?.result as string;
                            setSettings({ ...settings, company_logo: logoData });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    {settings.company_logo && (
                      <img src={settings.company_logo} alt="Logo preview" className="mt-2 h-16 object-contain" />
                    )}
                  </div>
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={settings.company_address}
                    onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={settings.company_phone}
                      onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={settings.company_email}
                      onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Website</Label>
                  <Input
                    value={settings.company_website}
                    onChange={(e) => setSettings({ ...settings, company_website: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Tagline</Label>
                  <Textarea
                    value={settings.tagline}
                    onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                    rows={2}
                  />
                </div>

                <Button onClick={saveSettings} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Company Info"}
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="media">
            <Card className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-4">Upload Cabinet Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {["shaker", "euro", "vanity", "general"].map((category) => (
                    <div key={category}>
                      <Label className="capitalize">{category}</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadMedia(file, category);
                        }}
                        disabled={uploading}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-medium mb-4">Your Media Library ({mediaLibrary.length} photos)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {mediaLibrary.map((media) => (
                    <div key={media.id} className="relative group">
                      <img
                        src={media.image_url}
                        alt={media.caption}
                        className="w-full h-40 object-cover rounded border"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteMedia(media.id, media.image_url)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 truncate">
                        {media.category} - {media.caption}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettings;
